import type { CurrencyDisplay } from './currency'

export type { CurrencyDisplay } from './currency'

export type Role = 'owner' | 'admin' | 'operator'

export interface User {
  id: number
  name: string
  role: Role
  pin: string
}

export interface ActiveSession {
  id: number
  tariffId: number
  startedAt: number
  plannedMinutes: number | null
  /** Сумма, уже принятая авансом (оплата по сумме). 0 если не было. */
  prepaidAmount: number
  /** Метод предоплаты: cash | card | mixed | null */
  prepaidMethod: PaymentMethod | null
}

export interface Table {
  id: number
  name: string
  relay: string
  tariffIds: number[]
  lightOn: boolean
  isActive: boolean
  isService: boolean
  isMaintenance: boolean
  activeSession: ActiveSession | null
  /**
   * Открытая задача на уборку: гости ушли, стол ещё не привели в порядок.
   * Заводится при закрытии игры, когда включены «Задачи на уборку»
   * (AppSettings.cleaningEnabled); снимается кнопкой «Уборка завершена».
   */
  cleaning: { id: number; startedAt: number } | null
}

export interface Tariff {
  id: number
  name: string
  pricePerHour: number
  color: string
  tableIds: number[]
  timeFrom: string | null
  timeTo: string | null
  days: number[]
}

/**
 * Штраф для клиента. Сумма не хранится — считается как `baseUnits` ×
 * `AppSettings.fineBaseValue` (базовая величина). Так при изменении базовой
 * пересчитываются все штрафы сразу.
 */
export interface Fine {
  id: number
  name: string
  /** Количество базовых величин (может быть дробным, напр. 0.5). */
  baseUnits: number
  /** Порядок в списке. */
  position: number
}

/**
 * Применённый клиенту штраф. Снимок суммы на момент выписки — сумма/название/
 * базовая фиксируются, чтобы изменение каталога или базовой не искажало историю.
 */
export interface FineCharge {
  id: number
  customerId: number
  /** Ссылка на штраф каталога (null, если штраф позже удалили). */
  fineId: number | null
  name: string
  baseUnits: number
  baseValue: number
  amount: number
  shiftId: number | null
  userId: number | null
  ts: number
  note: string | null
}

export type PaymentMethod = 'cash' | 'card' | 'mixed'
export type LoyaltyDiscountChoiceKind = 'none' | 'level' | 'promo' | 'personal'

export interface LoyaltyDiscountChoice {
  kind: LoyaltyDiscountChoiceKind
  promoId?: number
}

export interface PaymentLoyalty {
  customerId: number
  bonusSpent: number
  discount: LoyaltyDiscountChoice
  authorizedByUserId: number | null
}

export interface Payment {
  method: PaymentMethod
  cashAmount: number
  cardAmount: number
  loyalty?: PaymentLoyalty
  /**
   * Ручная скидка «из БО» (акция loyalty_promos) БЕЗ привязки клиента.
   * Требует подтверждения PIN администратора/владельца — проверяется повторно в main.
   * Взаимоисключима с клиентской скидкой и купоном (макс. один источник на чек).
   */
  manualPromo?: { promoId: number; authorizedByUserId: number }
  /** Код купона (скидка % или бесплатное время). Взаимоисключим со скидками. */
  couponCode?: string
}

export interface Session {
  id: number
  tableId: number
  startedAt: number
  endedAt: number | null
  tariffId: number
  amount: number
  plannedMinutes: number | null
  paymentMethod: PaymentMethod | null
  cashAmount: number
  cardAmount: number
  /** Сумма, принятая авансом до завершения сессии. */
  prepaidAmount: number
  prepaidMethod: PaymentMethod | null
  /** Сплит предоплаты: сколько из prepaidAmount пришло налом и безналом. */
  prepaidCashAmount: number
  prepaidCardAmount: number
  /**
   * Суммы, уже пробитые по фискальной кассе (нал/безнал). Фискальный чек выходит
   * только после закрытия всей оплаты; остаток к пробитию = (итог − fiscalized).
   */
  fiscalizedCash: number
  fiscalizedCard: number
  /** Клиент лояльности, привязанный при оплате (null — без клиента). */
  customerId: number | null
  /** Скидка/бонусы, зафиксированные при закрытии сессии (0 — не было). */
  discountAmount: number
  bonusUsed: number
  bonusEarned: number
  /** Применённый купон (null — не было). freeMinutes — списанные бесплатные минуты. */
  couponId: number | null
  couponFreeMinutes: number
  /** Часть discountAmount, приходящаяся на бар/кухню (двойная логика скидок, вариант Б). */
  barDiscountAmount: number
  /**
   * Сквозной номер чека, присваивается в момент, когда сессия становится чеком
   * (закрытие игры или продажа бара без игры). У ещё открытой игры — null.
   * Нумерация продолжает облачный «водяной знак», как у номеров смен, поэтому
   * переустановка терминала не начинает счёт заново.
   */
  receiptNumber: number | null
  /**
   * Последний фискальный документ по этой сессии. Нужен возврату: операция
   * MONEY_BACK не ссылается на исходный чек, но номер продажи показывается
   * кассиру и уходит в уведомление. Сессию можно пробить дважды (предоплата +
   * доплата) — fiscalDocCount говорит, сколько документов было; полная история
   * остаётся в журнале.
   */
  fiscalNumberCheck: string | null
  fiscalUid: string | null
  fiscalRegisterNumber: string | null
  fiscalDocCount: number
}

export type CouponKind = 'percent' | 'free_time'

/**
 * Потолок лимита применений у акций и купонов. Больше выставить нельзя — это уже
 * «без ограничения» (usageLimit = null).
 */
export const MAX_USAGE_LIMIT = 10_000_000

/** Купон лояльности: скидка % на чек ИЛИ бесплатное игровое время. */
export interface Coupon {
  id: number
  /** Короткий код для ввода кассиром (генерируется при выдаче). */
  code: string
  kind: CouponKind
  discountPercent: number
  freeMinutes: number
  note: string | null
  isActive: boolean
  issuedAt: number
  expiresAt: number | null
  redeemedAt: number | null
  redeemedSessionId: number | null
  /** Двойная логика скидок (вариант Б): распространять ли % скидки купона на бар/кухню. */
  barDiscountEnabled: boolean
  /**
   * Сколько раз купон можно применить (1…MAX_USAGE_LIMIT). По умолчанию 1 —
   * одноразовый купон, как было до появления лимита.
   */
  usageLimit: number
  /** Сколько раз уже применён; купон исчерпан при usedCount >= usageLimit. */
  usedCount: number
}

/**
 * Устройство зала — экран, привязанный к кассе по PIN (раздел «Устройства»).
 * Пока это экраны кухни и бара; список общий для терминала и бекофиса (синк).
 */
export type DeviceKind = 'kitchen_screen' | 'bar_screen'

export const DEVICE_KIND_LABEL: Record<DeviceKind, string> = {
  kitchen_screen: 'Экран кухни',
  bar_screen: 'Экран бара'
}

export interface Device {
  id: number
  /** Что за устройство и где стоит: «Планшет на раздаче», «ТВ у барной стойки». */
  name: string
  kind: DeviceKind
  /** Код привязки: его вводят на экране, чтобы он начал получать заказы. */
  pin: string
  isActive: boolean
  /**
   * Экран может отмечать готовые заказы переданными в доставку. Флаг на устройстве,
   * а не общий тумблер: доставку отмечает и кухонный экран, и барный, но не всякий.
   */
  deliveryEnabled: boolean
  /**
   * Код привязки для облачного трекера (tracker.8ballpos.by), формат XXXX-XXXX.
   * В отличие от `pin` (локальная сеть, 6 цифр, уникален лишь внутри клуба)
   * однозначен на всю платформу — поэтому его выдаёт облако. `null`, пока
   * устройство не съездило в облако (автономный терминал трекер не использует).
   */
  pairCode: string | null
  createdAt: number
  /** Когда экран последний раз выходил на связь (null — ни разу). */
  lastSeenAt: number | null
}

/** Тип игры стола (справочник заводится в бэкофисе, спускается на терминал). */
export interface GameType {
  /** Стабильный код — на него ссылается стол (table.kind) и бронь (gameKind). */
  code: string
  name: string
  color: string | null
  position: number
}

export interface Shift {
  id: number
  number: number
  openedAt: number
  closedAt: number | null
  openedByUserId: number
  closedByUserId: number | null
  revenue: number
  openingCash: number
  cashTotal: number
  cardTotal: number
  encashment: number
  /**
   * Фактический пересчёт денежного ящика при закрытии смены (обязательный ввод) и
   * снимок ожидаемой суммы на тот момент. Ожидаемую храним снимком: поздняя правка
   * чека иначе перепишет уже сданный Z-отчёт. Расхождение — производное
   * (см. cashDiscrepancy в cash.ts), не хранится. У смен, закрытых до появления
   * пересчёта, оба поля null — потребители показывают «—».
   */
  countedCash: number | null
  expectedCash: number | null
}

/** Внесение или изъятие наличных ВНУТРИ смены. */
export type CashOpKind = 'deposit' | 'withdraw'

export const CASH_OP_KIND_LABEL: Record<CashOpKind, string> = {
  deposit: 'Внесение',
  withdraw: 'Изъятие'
}

/** Стадия фискальной операции по кассе. */
export type FiscalOpState = 'skipped' | 'pending' | 'ok' | 'failed'

/**
 * Операция с наличными внутри смены. Внесение при ОТКРЫТии смены и изъятие при её
 * ЗАКРЫТИИ здесь НЕ живут — они остаются полями смены (opening_cash / encashment),
 * иначе получился бы двойной счёт во всех отчётах.
 */
export interface CashOp {
  id: number
  shiftId: number
  kind: CashOpKind
  amount: number
  reason: string | null
  userId: number | null
  /** Имя сотрудника — для списка в разделе «Смена». */
  userName: string | null
  ts: number
  /** Просил ли кассир фискальный чек (переключатель в окне операции). */
  fiscalRequested: boolean
  fiscalState: FiscalOpState
  fiscalNumberCheck: string | null
  fiscalError: string | null
}

export interface ShiftSessionRow {
  id: number
  tableId: number
  tableName: string
  tariffId: number
  tariffName: string
  startedAt: number
  endedAt: number | null
  durationMin: number
  amount: number
  cashAmount: number
  cardAmount: number
  paymentMethod: PaymentMethod | null
  plannedMinutes: number | null
  discountAmount: number
  bonusUsed: number
  /**
   * Сколько по чеку уже пробито по фискальной кассе (нал + безнал). Правка
   * способа оплаты в «Продажах» такой чек не переписывает — нужно предупредить.
   */
  fiscalizedAmount: number
  /** Сквозной номер чека (null у ещё открытой игры и у чеков до его появления). */
  receiptNumber: number | null
  /** Номер фискального документа продажи, если чек пробивался на кассе. */
  fiscalNumberCheck: string | null
}

export interface ShiftAggregates {
  count: number
  revenue: number
  cash: number
  card: number
  totalDiscount: number
  totalBonusUsed: number
  /**
   * Возвраты, проведённые В ЭТУ смену (в т.ч. по чекам прошлых смен). Выручка выше
   * остаётся брутто — вычитание живёт в netRevenue, чтобы отчёты сходились с чеками.
   */
  refundsCount: number
  refundsTotal: number
  refundsCash: number
  refundsCard: number
  /** Из них — по чекам ПРОШЛЫХ смен (входят в refundsTotal, показываем отдельно). */
  refundsForeignCount: number
  /** revenue − refundsTotal. Производное, не хранится. */
  netRevenue: number
}

export interface ShiftBreakdownRow {
  id: number
  name: string
  count: number
  amount: number
}

/** Возврат выбранных позиций или возврат по чеку суммой. */
export type RefundKind = 'position' | 'amount'

/** Откуда взялась возвращаемая строка. */
export type RefundItemSource = 'game' | 'bar' | 'inventory' | 'amount'

/** Строка возврата — снимок имени и цены на момент возврата. */
export interface RefundItem {
  id: number
  source: RefundItemSource
  /** Ссылка на возвращаемую позицию заказа бара (null у игры и возврата суммой). */
  barOrderItemId: number | null
  /** Ссылка на позицию инвентаря, если возвращают прокат (null у остальных). */
  inventoryItemId: number | null
  name: string
  price: number
  qty: number
  subtotal: number
}

/**
 * Возврат денег клиенту.
 *
 * Возвраты — ОТДЕЛЬНЫЙ регистр: они не меняют ни сумму чека, ни итоги смены
 * (иначе пересчёт при закрытии смены их бы уничтожил). Выручка остаётся брутто,
 * а «за вычетом возвратов» считается в отчётах.
 */
export interface Refund {
  id: number
  /** Сквозной номер возврата (продолжает облачный водяной знак, как номера чеков). */
  number: number
  sessionId: number | null
  /** Смена, которая физически отдала деньги — её касается расхождение ящика. */
  shiftId: number | null
  /** Смена исходного чека: возврат по чеку прошлой смены разрешён. */
  originShiftId: number | null
  kind: RefundKind
  amount: number
  cashAmount: number
  cardAmount: number
  reason: string
  userId: number | null
  userName: string | null
  /** Кто разрешил возврат (PIN владельца/администратора). */
  authorizedBy: number | null
  authorizedByName: string | null
  ts: number
  /** RRN операции с банковского слипа — касса требует его для возврата на карту. */
  rrn: string | null
  /** Пробивался ли возврат по кассе: 'none' — только внутренний учёт. */
  fiscalMode: 'none' | 'money_back'
  fiscalState: FiscalOpState
  fiscalNumberCheck: string | null
  fiscalError: string | null
  /** Номер исходного чека — для списка и уведомления. */
  receiptNumber: number | null
  items: RefundItem[]
}

/**
 * Позиция чека, доступная к возврату. Считается в main тем же разбором сумм, что
 * и состав фискального чека продажи, — чтобы расклад по позициям не разошёлся.
 */
export interface RefundableLine {
  /** Стабильный ключ строки (для выбора в интерфейсе и для передачи в create). */
  key: string
  source: RefundItemSource
  barOrderItemId: number | null
  inventoryItemId: number | null
  name: string
  price: number
  qty: number
  subtotal: number
  /** Сколько этой позиции уже возвращено ранее. */
  refundedQty: number
  refundedAmount: number
  /** Игровая строка возвращается суммой, а не количеством. */
  divisible: boolean
}

/** Результат фискальной операции возврата (та же форма, что у продажи). */
export type FiscalRefundResult = FiscalSaleResult

export interface ShiftDetail {
  shift: Shift
  openedByName: string | null
  closedByName: string | null
  sessions: ShiftSessionRow[]
  totals: ShiftAggregates
  byTariff: ShiftBreakdownRow[]
  byTable: ShiftBreakdownRow[]
  /** Операции с наличными внутри смены (внесения/изъятия), новые сверху. */
  cashOps: CashOp[]
  /** Возвраты, проведённые в эту смену, новые сверху. */
  refunds: Refund[]
  /** Сумма внесений и изъятий внутри смены — слагаемые формулы ожидаемых наличных. */
  cashDeposits: number
  cashWithdrawals: number
  /**
   * Ожидаемые наличные в ящике. Единственный источник этого числа для интерфейса:
   * считается через expectedCash (cash.ts). У ЗАКРЫТОЙ смены берётся снимок
   * shift.expectedCash, у открытой — считается на текущий момент.
   */
  expectedCash: number
  /** Расхождение пересчёта (факт − ожидание); null, если пересчёт не вводился. */
  cashDiscrepancy: number | null
}

export interface LoyaltyLevel {
  id: number
  name: string
  threshold: number
  discountPercent: number
  bonusPercent: number
  color: string
  /** Двойная логика скидок (вариант Б): распространять ли % скидки уровня на бар/кухню. */
  barDiscountEnabled: boolean
}

export interface Customer {
  id: number
  name: string
  phone: string | null
  cardCode: string | null
  qrCode: string | null
  bonusBalance: number
  levelId: number | null
  levelName: string | null
  discountPercent: number
  bonusPercent: number
  totalSpent: number
  createdAt: number
  notes: string | null
  tgUserId: number | null
  // Персональная скидка (переопределяет скидку уровня если больше)
  personalDiscountPercent: number // 0 = нет персональной скидки
  personalDiscountNote: string | null // описание/причина скидки
  /** Двойная логика скидок (вариант Б): действует ли скидка уровня клиента и на бар/кухню. */
  levelBarDiscountEnabled: boolean
  /** То же самое, но для персональной скидки клиента. */
  personalDiscountBarEnabled: boolean
}

export interface LoyaltyPromo {
  id: number
  name: string
  discountPercent: number
  bonusPercent: number
  timeFrom: string | null
  timeTo: string | null
  days: number[]
  activeFrom: number | null
  activeTo: number | null
  isActive: boolean
  /** Двойная логика скидок (вариант Б): распространять ли % скидки акции на бар/кухню. */
  barDiscountEnabled: boolean
  /**
   * Лимит применений акции (1…MAX_USAGE_LIMIT); null — без ограничения (поведение
   * до появления лимита). Исчерпанная акция не предлагается в окне оплаты.
   */
  usageLimit: number | null
  /** Сколько раз акция уже применена на этом терминале. */
  usedCount: number
}

export type LoyaltyTxKind = 'earn' | 'spend' | 'adjust'

export interface LoyaltyTx {
  id: number
  customerId: number
  shiftId: number | null
  sessionId: number | null
  kind: LoyaltyTxKind
  amount: number
  ts: number
  note: string | null
}

export interface LoyaltyDiscountOption {
  kind: 'level' | 'promo' | 'personal'
  promoId: number | null
  name: string
  discountPercent: number
  /** Двойная логика скидок (вариант Б): распространяется ли этот вариант скидки на бар/кухню. */
  barDiscountEnabled: boolean
}

export interface LoyaltyQuote {
  customer: Customer
  bonusPercent: number
  bonusBalance: number
  availableDiscounts: LoyaltyDiscountOption[]
}

export interface InventoryItem {
  id: number
  name: string
  price: number
  deposit: number
  stock: number
  isConsumable: boolean
  isActive: boolean
}

export type InventoryTxKind = 'issue' | 'return' | 'consume' | 'adjust'

export interface InventoryTx {
  id: number
  itemId: number
  itemName: string
  tableId: number | null
  sessionId: number | null
  kind: InventoryTxKind
  qty: number
  priceCharged: number
  ts: number
  userId: number | null
  note: string | null
}

export interface InventoryRented {
  itemId: number
  itemName: string
  isConsumable: boolean
  outstandingQty: number
  issuedQty: number
  charged: number
  depositCharged: number
}

export type JournalKind =
  | 'shift'
  | 'tariff'
  | 'table'
  | 'user'
  | 'system'
  | 'hardware'
  /** Сервисный режим / тех. работы по столу (источник Telegram-уведомления в облаке). */
  | 'service'
  /** Правка закрытого чека (например, ошиблись способом оплаты). */
  | 'receipt'

export interface JournalEntry {
  id: number
  ts: number
  kind: JournalKind
  message: string
  userId: number | null
}

export type PrinterMode = 'off' | 'thermal' | 'system'
export type PrinterWidth = 58 | 80

/** Роль принтера — какие документы на него отправляются. */
export type PrinterRole = 'receipts' | 'reports' | 'bar' | 'kitchen' | 'precheck'

/** Конфигурация одного принтера (мультипринтерная модель, таблица `printers`). */
export interface PrinterConfig {
  id: string
  label: string
  mode: PrinterMode
  host: string
  port: number
  name: string
  width: PrinterWidth
  roles: PrinterRole[]
}

/** Одна строка заказа бара/кухни для чека. */
export interface BarOrderItemPayload {
  name: string
  /** Объём/порция — отдельной строкой под названием. */
  portion?: string | null
  qty: number
  price: number
  subtotal: number
}

/** Пэйлоад чека бара (с ценами, роль bar). */
export interface PrinterBarPayload {
  clubName: string
  orderNumber: number
  tableName: string
  bartenderName: string
  shiftNumber: number
  createdAt: number
  items: BarOrderItemPayload[]
  total: number
  unp?: string
  orgName?: string
  currencyDisplay?: CurrencyDisplay
}

/** Пэйлоад чека кухни (без цен — только позиции и количество, роль kitchen). */
export interface PrinterKitchenPayload {
  clubName: string
  orderNumber: number
  tableName: string
  bartenderName: string
  shiftNumber: number
  createdAt: number
  /** Объём/порция печатается подстрочником под названием — повару он важен. */
  items: Array<{ name: string; portion?: string | null; qty: number }>
}

/**
 * Пэйлоад чека возврата (роль receipts). Нефискальный: фискальный документ, если
 * касса настроена, печатает сама касса, а этот — то, что кассир отдаёт гостю.
 */
export interface PrinterRefundPayload {
  clubName: string
  venueName?: string
  orgName?: string
  unp?: string
  /** Внутренний номер возврата. */
  refundNumber: number
  /** Номер чека, по которому делается возврат (может отсутствовать у старых чеков). */
  receiptNumber: number | null
  ts: number
  cashierName: string
  reason: string
  lines: Array<{ name: string; portion?: string | null; qty: number; subtotal: number }>
  amount: number
  cashAmount: number
  cardAmount: number
  /** Номер фискального чека возврата, если касса его пробила. */
  fiscalNumberCheck: string | null
  /** Касса настроена, но документ не прошёл — печатаем предупреждение. */
  fiscalFailed: boolean
  currencyDisplay?: CurrencyDisplay
}

/** Пэйлоад предчека (игра + бар/кухня + итог, роль precheck). */
export interface PrinterPreCheckPayload {
  clubName: string
  tableName: string
  bartenderName: string
  shiftNumber: number
  printedAt: number
  tariffName: string
  pricePerHour: number
  startedAt: number
  durationMin: number
  gameAmount: number
  barItems: BarOrderItemPayload[]
  barTotal: number
  totalAmount: number
  unp?: string
  orgName?: string
  venueName?: string
  logoBase64?: string
  currencyDisplay?: CurrencyDisplay
}

export interface AppSettings {
  clubName: string
  telegramToken: string
  telegramChatId: string
  telegramNotifyShiftOpen: boolean
  telegramNotifyShiftClose: boolean
  telegramNotifyTariffChange: boolean
  /** Уведомлять о постановке стола в сервис / тех. работы (и о снятии). */
  telegramNotifyTableService: boolean
  /** Уведомлять в Telegram о возвратах денег клиенту. */
  telegramNotifyRefund: boolean
  kioskMode: boolean
  theme: 'dark' | 'light'
  telegramBotEnabled: boolean
  printerMode: PrinterMode
  printerHost: string
  printerPort: number
  printerName: string
  printerWidth: PrinterWidth
  printerAutoReceipt: boolean
  ikassaEnabled: boolean
  // Фискальная касса IKassa Smart X (локальный HTTP-сервер на host:37015)
  ikassaCloud: boolean // облачный режим Smart X: привязка и авторизация на самой кассе, PIN не нужен
  ikassaHost: string // IP/host кассы в локальной сети
  ikassaPort: number // порт HTTP-сервера кассы (по умолчанию 37015)
  ikassaPin: string // PIN кассира для авторизации (AUTH); не требуется для облачной кассы
  ikassaCashier: string // имя кассира для чека (поле cashier в SALE)
  // Реквизиты организации
  unp: string
  orgName: string
  venueName: string
  logoBase64: string
  // Инструкции из Notion (URL страницы или embedded-ссылка)
  notionUrl: string
  // Контакты реле PowerAnt в порядке физической проводки столов (буквы A–Z); пусто = стандарт A–H
  relayLetters: string[]
  // Как показывать валюту: знак (SVG) / код BYN / бел.руб. Сумма всегда в BYN.
  currencyDisplay: CurrencyDisplay
  // Базовая величина для расчёта штрафов (в BYN). Редактируют владелец/админ.
  fineBaseValue: number
  // Экран кухни (KDS, ресторанный режим): локальный сервер терминала раздаёт
  // страницу с тикетами на планшет в той же сети. Пока выключен, кухонные позиции
  // печатаются на принтере с ролью 'kitchen', как раньше.
  kdsEnabled: boolean
  kdsPort: number
  /**
   * Облачный экран (tracker.8ballpos.by): касса отдаёт тикеты в облако, а экран
   * подключается по интернету — нужен, когда кухня в другой сети или в другом
   * заведении. Локальный экран при этом продолжает работать как раньше.
   */
  trackerEnabled: boolean
  /**
   * Задачи на уборку: после каждой игры оператор получает задачу «убрать стол»
   * и закрывает её кнопкой. Без этого сведения о простое между гостями нет
   * вообще — ни на кассе, ни в отчётах.
   */
  cleaningEnabled: boolean
  /** Не отдавать стол под новую игру, пока уборка не закрыта. */
  cleaningBlocksTable: boolean
  /**
   * Блокировка терминала — защита открытой кассы, когда оператор отошёл.
   * Экран блокировки закрывает интерфейс, снимается PIN-кодом (как вход).
   * Смена, свет и таймеры столов при этом продолжают идти — блокируется только UI.
   */
  lockOnMinimize: boolean
  lockIdleEnabled: boolean
  /** Бездействие до блокировки, мин. Допустимые значения — LOCK_IDLE_CHOICES. */
  lockIdleMinutes: number
  /**
   * Звук и тост о новом ответе поддержки (Help #17). Настройка этой кассы:
   * в клубе может быть несколько касс, и звенеть нужна не каждой.
   */
  supportReplySound: boolean
}

/** Категории обращения в поддержку (форма на кассе и в бекофисе). */
export const SUPPORT_CATEGORIES = ['question', 'problem', 'idea', 'billing'] as const
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number]

/**
 * Обращение в поддержку, каким его видит касса. Живёт в локальной БД терминала
 * даже после отправки: свои вопросы должны открываться и без интернета.
 */
export interface SupportRequest {
  id: number
  subject: string
  message: string
  category: SupportCategory
  contact: string | null
  authorName: string
  createdAt: number
  /** null — ещё в очереди на отправку (нет связи с облаком). */
  sentAt: number | null
  /** Номер обращения в Help — его называют при звонке. */
  helpdeskTicketId: string | null
  lastError: string | null
}

/**
 * Обращение так, как его видит облако: со статусом в Help и перепиской.
 * Локальная копия (SupportRequest) ответов не знает — она нужна, чтобы обращение
 * не потерялось офлайн, а переписка приезжает сверху.
 */
export interface SupportCloudTicket {
  id: string
  subject: string
  message: string
  category: SupportCategory
  authorName: string
  source: string
  status: string
  helpdeskTicketId: string | null
  helpdeskStatus: string | null
  /** Подпись статуса из Help (контракт v2) — показывать её, а не ключ. */
  helpdeskStatusLabel?: string | null
  /** Статус финальный (решён/закрыт). null — Help ещё не сообщал. */
  helpdeskResolved?: boolean | null
  lastReplyAt: number | null
  createdAt: number
  messages: {
    id: string
    author: string
    fromAgent: boolean
    body: string
    createdAt: number
    /** uid ответа на кассе — по нему локальная очередь узнаёт свой уже доехавший ответ. */
    clientMessageId?: string | null
    /** Доставка ответа клуба в Help: queued | sent | failed (у ответов агента — null). */
    deliveryStatus?: string | null
  }[]
}

/**
 * Ответ клуба в обращении, каким его хранит касса: сначала локально, потом в
 * облако — та же офлайн-очередь, что и у самих обращений.
 */
export interface SupportReply {
  id: number
  /** Ключ идемпотентности: облако не задвоит ответ, если касса дошлёт его повторно. */
  uid: string
  cloudTicketId: string
  body: string
  authorName: string
  createdAt: number
  /** null — ещё в очереди. */
  sentAt: number | null
  lastError: string | null
  /** Облако отказало по существу (обращение не найдено) — больше не досылаем. */
  rejected: boolean
}

/** Задача, поставленная клубу поддержкой (живёт в Help, здесь — только показ). */
export interface SupportTask {
  id: number
  title: string
  description: string | null
  priority: string
  dueDate: string | null
  createdBy: string | null
}

/** Варианты задержки автоблокировки (минуты) — выпадающий список в настройках. */
export const LOCK_IDLE_CHOICES = [5, 10, 15, 30, 40] as const

/** Состояние локального сервера экрана кухни. */
export interface KdsState {
  enabled: boolean
  running: boolean
  port: number | null
  /** Состояние канала облачного трекера (см. AppSettings.trackerEnabled). */
  relay: { enabled: boolean; connected: boolean; screens: number; lastError: string | null }
  /** Адреса, которые открывают на планшете кухни (LAN-интерфейсы терминала). */
  urls: string[]
}

/**
 * Тикет экрана кухни: одна позиция кухонного заказа. Живёт в SQLite терминала —
 * KDS должен работать в локальной сети без облака и без задержек синка.
 */
export interface KitchenTicket {
  id: number
  orderId: number
  barItemId: string
  name: string
  /** Объём/порция («3 л») — бармену это нужно не меньше, чем название. */
  portion: string | null
  qty: number
  tableLabel: string | null
  /** Секция экрана: кухонные позиции показывает кухня, барные — бар. */
  section: 'bar' | 'kitchen'
  status: 'queued' | 'cooking' | 'ready' | 'served'
  /** Статус исполнения ЗАКАЗА — по нему экран решает, показывать ли доставку. */
  orderFulfillment: BarOrderFulfillment
  /** Норматив приготовления блюда, мин (из меню) — по нему KDS красит карточку. */
  prepTimeMinutes: number | null
  createdAt: number
  startedAt: number | null
  readyAt: number | null
  servedAt: number | null
}

export interface FiscalSaleResult {
  ok: boolean
  numberCheck?: string
  uid?: string
  registerNumber?: string
  error: string | null
}

export interface FiscalStatus {
  configured: boolean
  lastError: string | null
  registerNumber: string | null
}

export interface AppInfo {
  version: string
  developer: string
}

/** Стадия процесса обновления приложения (electron-updater). */
export type UpdateState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'not-available'
  | 'downloading'
  | 'downloaded'
  | 'error'

/** Снимок состояния обновления; отдаётся по запросу и пушится при изменениях. */
export interface UpdateStatus {
  state: UpdateState
  /** Установленная сейчас версия. */
  currentVersion: string
  /** Версия из фида, если найдена (новее текущей). */
  newVersion: string | null
  /** Прогресс загрузки, 0–100. */
  percent: number
  /** Скачано байт. */
  transferred: number
  /** Всего байт. */
  total: number
  /** Скорость загрузки, байт/с. */
  bytesPerSecond: number
  /** Текст последней ошибки (если state === 'error'). */
  error: string | null
  /** Источник обновлений зашит в приложение — обновляться всегда есть откуда. */
  configured: boolean
  /** Зашитый адрес фида, из которого тянутся обновления (для показа в «Настройках»). */
  source: string
}

export interface PrinterReceiptPayload {
  clubName: string
  tableName: string
  tariffName: string
  startedAt: number
  endedAt: number
  durationMin: number
  pricePerHour: number
  amount: number
  amountBeforeDiscount: number
  discountAmount: number
  discountLabel: string
  bonusSpent: number
  cashAmount: number
  cardAmount: number
  method: PaymentMethod
  shiftNumber: number
  receiptNumber: number
  bartenderName: string
  unp: string
  orgName: string
  venueName: string
  logoBase64: string
  /** Как подписывать валюту на чеке; задаётся main из настроек. По умолчанию 'code'. */
  currencyDisplay?: CurrencyDisplay
  /** Подзаголовок чека (например «ПРЕДОПЛАТА»); по умолчанию «Бильярдный клуб». */
  docTitle?: string
  /** Доп. строка в блоке скидок (например «Купон: 60 мин бесплатно»). */
  noteLine?: string
  /** Позиции бара/кухни по этой сессии — печатаются отдельным блоком перед итогом. */
  barLines?: {
    name: string
    /** Объём/порция — печатается отдельной строкой под названием. */
    portion?: string | null
    qty: number
    price: number
    subtotal: number
  }[]
  /** Сумма скидки, применённой к бару/кухне (вариант Б — тот же % что и на стол, при включённом флаге). */
  barDiscountAmount?: number
}

export interface PrinterReportPayload {
  clubName: string
  kind: 'X' | 'Z'
  shiftNumber: number
  openedAt: number
  closedAt: number | null
  openedByName: string | null
  closedByName: string | null
  openingCash: number
  encashment: number
  totals: ShiftAggregates
  byTariff: ShiftBreakdownRow[]
  byTable: ShiftBreakdownRow[]
  unp: string
  orgName: string
  venueName: string
  bartenderName: string
  totalDiscountAmount: number
  totalBonusSpent: number
  /** Как подписывать валюту в отчёте; задаётся main из настроек. По умолчанию 'code'. */
  currencyDisplay?: CurrencyDisplay
  /** Внесения и изъятия наличных ВНУТРИ смены (0 — не было). */
  cashDeposits: number
  cashWithdrawals: number
  /**
   * Сверка денежного ящика: ожидаемая сумма, фактический пересчёт и расхождение.
   * У X-отчёта и у смен, закрытых до появления пересчёта, пересчёт/расхождение — null.
   */
  expectedCash: number | null
  countedCash: number | null
  cashDiscrepancy: number | null
  /** Возвраты, проведённые в смену, и выручка за их вычетом (0 — возвратов не было). */
  refundsTotal: number
  refundsCount: number
  netRevenue: number
}

export interface TelegramBotInfo {
  id: number
  username: string
  firstName: string
}

export interface SerialStatus {
  connected: boolean
  port: string | null
  baud: number
  lastError: string | null
}

/**
 * Бронь в представлении терминала. Брони двунаправленны: спускаются из облака
 * (pull, read-only показ) И создаются/правятся прямо на терминале (push в облако).
 * `source` — происхождение брони ('terminal' | 'public' | …), показывается оператору.
 */
export interface TerminalBooking {
  id: number
  tableId: number | null
  tableName: string | null
  /** Код типа игры (game_types.code) или null. */
  gameKind: string | null
  /** Карточка клиента (заводится/находится по телефону) — null у анонимной брони. */
  customerId: number | null
  customerName: string
  phone: string | null
  startsAt: number
  endsAt: number
  status: string
  source: string | null
}

/** Ввод создания брони на терминале (стол + клиент + интервал времени). */
export interface BookingCreateInput {
  tableId: number
  gameKind?: string | null
  customerName: string
  phone: string
  startsAt: number
  endsAt: number
  notes?: string | null
  status?: string
}

/** Частичный ввод правки/переноса брони на терминале. */
export interface BookingUpdateInput {
  tableId?: number
  gameKind?: string | null
  customerName?: string
  phone?: string
  startsAt?: number
  endsAt?: number
  notes?: string | null
  status?: string
}

/**
 * Уведомление о НОВОЙ брони (пришла из облака — например, клиент забронировал на
 * публичном сайте). Показывается оператору тостом + коротким звуком на любой странице.
 */
export interface NewBookingNotice {
  customerName: string
  tableName: string | null
  startsAt: number
  endsAt: number
  source: string | null
}

export type PageKey =
  | 'tables'
  | 'shift'
  | 'comport'
  | 'tariffs'
  | 'users'
  | 'journal'
  | 'shifts-history'
  | 'telegram'
  | 'settings'
  | 'customers'
  | 'loyalty'
  | 'inventory'
  | 'sales'
  | 'bookings'
  | 'instructions'

/** Состояние конфигурации облачного синка (для UI настроек терминала). */
export interface SyncConfigState {
  /** Включён ли синк оператором. */
  enabled: boolean
  /** Базовый URL облачного бэкенда. */
  baseUrl: string
  /** Задан ли device-токен (само значение наружу не отдаётся). */
  hasToken: boolean
  /** Синк фактически активен (enabled + заданы baseUrl и токен). */
  active: boolean
}

/** Ввод настроек облачного синка из UI. */
export interface SetSyncConfigInput {
  enabled: boolean
  baseUrl: string
  /** Непустой → обновить device-токен; пустой/отсутствует → сохранить прежний. */
  deviceToken?: string
}

/**
 * Ввод проверки подключения. Переопределяет сохранённые значения, чтобы можно
 * было проверить ДО сохранения; пустые/отсутствующие → берётся сохранённое.
 */
export interface SyncTestInput {
  baseUrl?: string
  deviceToken?: string
}

/** Результат проверки подключения к облаку (POST /v1/sync/hello). */
export interface SyncTestResult {
  ok: boolean
  /** HTTP-код ответа сервера, если он ответил. */
  status?: number
  /** Человекочитаемое сообщение для UI. */
  message: string
  /** Рассинхрон часов терминал↔сервер (мс) — при успехе. */
  skewMs?: number
}

/**
 * Результат немедленной синхронизации (sync:now), которую терминал выполняет
 * блокирующе перед открытием смены. Если синк не настроен (standalone) —
 * active=false и ok=true (синхронизировать нечего, открытие смены продолжается).
 */
export interface SyncNowResult {
  /** Цикл push+pull завершился без ошибок (или синхронизировать нечего). */
  ok: boolean
  /** Синк настроен и активен (enabled + baseUrl + токен). */
  active: boolean
  /** Сообщение об ошибке для UI — при ok=false. */
  message?: string
}

/** Текущий статус лицензии терминала (читается из sync_state). */
export interface LicenseStatus {
  /** Терминал активирован (device_token задан). */
  activated: boolean
  /** Срок действия лицензии (epoch-ms). null = не задан. */
  licenseExpiresAt: number | null
  /** Дней до истечения (по концу дня истечения; отрицательный = уже истекло). null = не задан. */
  daysLeft: number | null
  /** Момент жёсткой блокировки — конец дня истечения (epoch-ms, локальное время). null = не задан. */
  blockAtMs: number | null
  /** Лицензия истекла и наступила блокировка (now > blockAtMs). */
  blocked: boolean
  /**
   * Лицензия ОТОЗВАНА облаком (бэкенд вернул 401 на синхронизацию). Отдельно от
   * blocked (истечение срока): отзыв — намеренное действие лицензиара. Сбрасывается
   * после успешного hello (бэкенд снова авторизовал устройство).
   */
  revoked: boolean
  /** Статус заявки на продление. */
  renewalStatus: 'none' | 'pending'
  /** Название клуба, если известно. */
  clubName: string | null
  /** Контакты поддержки (из облака; null = ещё не синкнуто). */
  supportContacts: string | null
}

/** Результат активации терминала по лицензионному ключу. */
export interface LicenseActivateResult {
  ok: boolean
  error?: string
  clubName?: string
  licenseExpiresAt?: number
}

/**
 * Ссылка на оплату подписки картой (POST /v1/license/pay-link). Облако находит
 * неоплаченный счёт клуба или выставляет новый на месяц, а терминал открывает
 * `url` в системном браузере — оплата идёт на стороне bePaid.
 */
export interface LicensePayLinkResult {
  ok: boolean
  error?: string
  url?: string
  invoiceNumber?: string
  amountDue?: number
}

/**
 * Данные онбординга нового клуба (самостоятельная регистрация при первом запуске).
 * Реквизиты компании + карточка клуба для каталога/страницы бронирования.
 * Все поля, кроме названия и slug, необязательны. Логотип — загруженный файл
 * (clubLogo) либо ссылка на чужой хост (clubLogoUrl).
 * Те же ключи используются в облачных settings (см. booking-config на бэкенде).
 */
export interface OnboardClubInput {
  /** Название клуба — идёт и в clubs.name, и в settings.clubName. */
  clubName: string
  /** Публичный slug страницы бронирования (уникальный, [a-z0-9-]). */
  publicSlug: string
  /** Часовой пояс клуба (напр. Europe/Minsk), необязательно. */
  timezone?: string
  /** Краткое описание для карточки в каталоге. */
  clubDescription?: string
  /** URL логотипа для карточки в каталоге (ссылка на чужой хост). */
  clubLogoUrl?: string
  /**
   * Логотип файлом: `data:image/png;base64,…`. Приоритетнее clubLogoUrl —
   * бэкенд сохранит картинку у себя и подставит её адрес. Терминал ужимает
   * изображение перед отправкой (см. Onboarding.tsx).
   */
  clubLogo?: string
  /** Принимать онлайн-брони с публичной страницы (по умолчанию да). */
  publicBookingEnabled?: boolean
  // ── Реквизиты компании ──
  orgName?: string
  clubCountry?: string
  clubCity?: string
  clubAddress?: string
  unp?: string
  clubCurrency?: string
  clubWebsite?: string
  clubEmail?: string
  clubPhone?: string
  shiftEndTime?: string
  /** Код приглашения — нужен, только если поставщик включил защиту онбординга. */
  inviteCode?: string
}

/** Результат самостоятельного онбординга клуба (терминал сохраняет токен сам). */
export interface OnboardResult {
  ok: boolean
  error?: string
  clubName?: string
  publicSlug?: string
  /** Срок пробной лицензии (epoch-ms), выданной при онбординге. */
  licenseExpiresAt?: number
}

/** Категория меню бара/кухни (каталог синкается из облака; на терминале read-only). */
export interface BarCategory {
  id: string
  name: string
  section: 'bar' | 'kitchen'
  position: number
  isActive: boolean
}

/** Позиция меню бара/кухни (каталог синкается из облака; на терминале read-only). */
export interface BarItem {
  id: string
  categoryId: string
  name: string
  description: string | null
  price: number
  position: number
  isActive: boolean
  /** Ручной стоп блюда (ресторанный режим), выставляется в бэкофисе. */
  isStopped: boolean
  stopReason: string | null
  /**
   * Недоступно к продаже: ручной стоп ИЛИ стоп любого ингредиента техкарты.
   * Флаг считает облако (терминалу техкарты не спускаются) и присылает синком.
   */
  isUnavailable: boolean
  /** Штрихкод фасованного товара, который продаётся «как есть» (скан → в заказ). */
  barcode: string | null
  /** Норматив приготовления, мин — порог таймера на экране кухни (KDS). */
  prepTimeMinutes: number | null
  /**
   * Объём или порция: «0,5 л», «3 л», «250 г», «2 шт». Свободный текст, потому
   * что в меню соседствуют литры, граммы и штуки, а считать по нему ничего не
   * нужно — себестоимость даёт техкарта. Задаётся в бекофисе, вниз едет синком.
   * В строку заказа попадает склеенным с названием (см. barItemLabel).
   */
  portion: string | null
  /**
   * Картинка позиции (адрес в облачном хранилище, см. модуль uploads).
   * Задаётся в бекофисе, вниз едет синком; касса показывает миниатюру в каталоге.
   * null — картинки нет, плитка рисуется как раньше, одним текстом.
   */
  imageUrl: string | null
}

/**
 * Результат скана штрихкода на терминале. Один код может означать разное:
 *  - 'menu'  — фасованный товар из меню: добавляем в заказ;
 *  - 'stock' — ингредиент склада: продать напрямую нельзя, показываем остаток;
 *  - 'none'  — код неизвестен.
 */
export type BarcodeScanResult =
  | { kind: 'menu'; item: BarItem }
  | { kind: 'stock'; item: StockItem }
  | { kind: 'none' }

/**
 * Складская позиция (ресторанный режим), спущенная из облака только для чтения:
 * лукап по штрихкоду и показ остатка/стопа кассиру. Списание и себестоимость
 * считает облако — терминал склад не меняет.
 */
export interface StockItem {
  id: string
  name: string
  section: 'bar' | 'kitchen'
  unit: 'pcs' | 'kg' | 'g' | 'l' | 'ml'
  barcode: string | null
  qty: number
  minQty: number
  isStopped: boolean
  stopReason: string | null
  isActive: boolean
}

/**
 * Статус ИСПОЛНЕНИЯ заказа бара/кухни. Не путать с `BarOrder.status` — тот про
 * оплату (открыт/закрыт). Статус хранится на заказе, а не выводится из тикетов:
 * тикетов может не быть вовсе (заказ ушёл на кухонный принтер), а «передан в
 * доставку» и «возвращён» — факты уровня заказа, у которых нет представления в
 * per-item тикете.
 */
export type BarOrderFulfillment =
  | 'none'
  | 'sent'
  | 'cooking'
  | 'ready'
  | 'delivery'
  | 'served'
  | 'refunded'

export const BAR_ORDER_FULFILLMENT_LABEL: Record<BarOrderFulfillment, string> = {
  none: 'Без кухни',
  sent: 'Передан на кухню',
  cooking: 'Готовится',
  ready: 'Готов',
  delivery: 'Передан в доставку',
  served: 'Отдан',
  refunded: 'Возвращён'
}

/** Строка заказа бара — снимок имени/цены позиции на момент оформления. */
export interface BarOrderLine {
  barItemId: string
  name: string
  /**
   * Объём/порция на момент продажи («0,5 л»). Снимок, как name и price: позицию
   * меню могли потом переименовать или удалить, а чек и возврат ссылаются на то,
   * что реально продали. Отдельным полем — в чеке печатается отдельной строкой.
   */
  portion: string | null
  price: number
  qty: number
  subtotal: number
}

/** Заказ бара/кухни по сессии стола. Итог добавляется к сумме при закрытии стола. */
export interface BarOrder {
  id: number
  tableId: number | null
  sessionId: number | null
  /** Платёжный статус: открыт (войдёт в счёт стола) или закрыт (оплачен). */
  status: 'open' | 'closed'
  /** Статус исполнения: где заказ на кухне/в баре. */
  fulfillment: BarOrderFulfillment
  total: number
  createdAt: number
  items: BarOrderLine[]
}

/** Строка списка заказов на вкладке «Заказы» (с меткой стола и числом позиций). */
export interface BarOrderRow extends BarOrder {
  /** «Стол 3» или null для продажи без игры. */
  tableLabel: string | null
  itemsCount: number
}

export interface ApiSurface {
  auth: {
    listUsers: () => Promise<Pick<User, 'id' | 'name' | 'role'>[]>
    verify: (userId: number, pin: string) => Promise<User | null>
    verifyAdminPin: (pin: string) => Promise<Pick<User, 'id' | 'name' | 'role'> | null>
    create: (input: { name: string; role: Role; pin: string }) => Promise<User>
    update: (id: number, patch: Partial<Omit<User, 'id'>>) => Promise<User>
    remove: (id: number) => Promise<void>
  }
  tables: {
    list: () => Promise<Table[]>
    create: (input: { name: string; relay: string }) => Promise<Table>
    update: (id: number, patch: Partial<Table>) => Promise<Table>
    remove: (id: number) => Promise<void>
    reorder: (orderedIds: number[]) => Promise<Table[]>
    setLight: (id: number, on: boolean) => Promise<Table>
    /** Закрыть задачу на уборку стола («Уборка завершена»). */
    finishCleaning: (id: number, userId?: number | null) => Promise<Table>
    start: (
      id: number,
      userId: number,
      tariffId: number,
      plannedMinutes?: number | null,
      /**
       * Клиент, известный ДО игры (запуск по брони). Обычно клиента привязывают при
       * оплате, но у брони он уже есть — тогда посещение попадёт в его историю даже
       * если кассир не стал привязывать карточку в окне «Стоп».
       */
      customerId?: number | null
    ) => Promise<{ table: Table; session: Session }>
    stop: (
      id: number,
      userId: number,
      payment: Payment,
      /**
       * Момент закрытия сессии (для тарификации). По умолчанию — «сейчас». Терминал
       * передаёт сюда момент нажатия «Стоп» (заморозки), чтобы время раздумий кассира
       * при приёме оплаты не тарифицировалось — сумма совпадает с замороженной на экране.
       */
      closeAt?: number | null
    ) => Promise<{
      table: Table
      session: Session
      /**
       * Нефискальный чек НЕ напечатан — его печатает рендерер после уведомления
       * кассира об успешной оплате картой. false, если печатать нечего или чек уже
       * ушёл на принтер.
       */
      receiptDeferred: boolean
    }>
    extend: (
      tableId: number,
      addMinutes: number,
      userId: number
    ) => Promise<{ table: Table; session: Session }>
    /**
     * Принять частичную оплату (предоплату) — стол остаётся активным.
     * Части фискально НЕ пробиваются (печатается нефискальный чек оплаты).
     * Если после этой части оплата полностью закрывает сумму карточки (пакет),
     * main пробивает наличный остаток фоном, а безнал возвращает в `fiscalDue` —
     * рендерер фискализирует его синхронно через fiscal:printSale (окно ожидания).
     */
    partialPay: (
      tableId: number,
      userId: number,
      payment: Payment
    ) => Promise<{
      table: Table
      session: Session
      /** Остаток к синхронной фискализации (есть безнал) или null. */
      fiscalDue: { cash: number; cashless: number } | null
    }>
    /**
     * «Возврат паузы»: сдвинуть начало активной сессии вперёд на `pausedMs`, чтобы
     * это время не тарифицировалось (напр. пока открыта модалка «Оплата по сумме»).
     * Дедлайн авто-стопа сдвигается на ту же величину.
     */
    pauseResume: (
      tableId: number,
      pausedMs: number,
      userId: number
    ) => Promise<{ table: Table; session: Session }>
    /** userId — кто переключил (для журнала и Telegram-уведомления); null = система. */
    setService: (id: number, isService: boolean, userId?: number | null) => Promise<Table>
    setMaintenance: (id: number, isMaintenance: boolean, userId?: number | null) => Promise<Table>
    serviceAllOff: (userId: number | null) => Promise<Table[]>
    onChanged: (cb: () => void) => () => void
    /** Подписка на истечение времени стола (звук + красный UI). Возвращает функцию отписки. */
    onTimeExpired: (cb: (d: { tableId: number; sessionId: number }) => void) => () => void
  }
  tariffs: {
    list: () => Promise<Tariff[]>
    upsert: (input: Omit<Tariff, 'id'> & { id?: number }, userId?: number | null) => Promise<Tariff>
    remove: (id: number) => Promise<void>
  }
  fines: {
    list: () => Promise<Fine[]>
    upsert: (input: Omit<Fine, 'id' | 'position'> & { id?: number }) => Promise<Fine>
    remove: (id: number) => Promise<void>
    /** Применить штраф к клиенту (запись в историю + журнал). */
    charge: (input: {
      customerId: number
      fineId: number
      userId?: number | null
      note?: string | null
    }) => Promise<FineCharge>
    chargesForCustomer: (customerId: number) => Promise<FineCharge[]>
  }
  shifts: {
    current: () => Promise<Shift | null>
    open: (userId: number, openingCash: number) => Promise<Shift>
    /**
     * Закрытие смены. `countedCash` — фактический пересчёт денежного ящика,
     * обязателен: без него не посчитать расхождение, ради которого пересчёт и вводят.
     */
    close: (userId: number, encashment: number, countedCash: number) => Promise<Shift>
    list: (limit?: number) => Promise<Shift[]>
    detail: (shiftId?: number) => Promise<ShiftDetail | null>
    /**
     * Правка способа оплаты закрытого чека («Продажи → Чеки»): приняли не тем
     * методом. Сумма чека не меняется — только раскладка нал/безнал. Доступно
     * владельцу и администратору; пишется в журнал (kind `receipt`).
     */
    updateReceiptPayment: (
      sessionId: number,
      patch: {
        method: PaymentMethod
        /** Наличная часть; для 'cash'/'card' сплит считает main по сумме чека. */
        cashAmount?: number
        /** Причина правки — уходит в журнал. */
        comment?: string | null
      },
      userId: number
    ) => Promise<Session>
  }
  /**
   * Возвраты денег клиенту. Возврат — отдельный регистр: сумма чека и итоги смены
   * не меняются, «за вычетом возвратов» считается в отчётах.
   */
  refunds: {
    /** Возвраты, проведённые в смену (по умолчанию — в текущую). */
    listForShift: (shiftId?: number) => Promise<Refund[]>
    /** Возвраты по конкретному чеку — показываем в его карточке. */
    listForSession: (sessionId: number) => Promise<Refund[]>
    /**
     * Позиции чека, доступные к возврату, с учётом ранее возвращённого.
     * Считает main тем же разбором сумм, что и состав фискального чека.
     */
    linesForSession: (sessionId: number) => Promise<RefundableLine[]>
    /**
     * Оформить возврат. Сначала пишется внутренняя запись, затем пробивается
     * фискальный чек (если касса настроена): документ без записи не сверить, а
     * запись без документа видна кассиру и повторяема через fiscal.retryRefund.
     */
    create: (input: {
      sessionId: number
      kind: RefundKind
      /** Ключи строк из linesForSession и количество (для делимой строки — сумма). */
      lines: Array<{ key: string; qty: number }>
      /** Для kind = 'amount' — сумма возврата по чеку. */
      amount?: number
      cashAmount: number
      cardAmount: number
      reason: string
      userId: number
      /** Подтвердивший возврат владелец/администратор (PIN-эскалация оператора). */
      authorizedBy: number | null
      /** RRN со слипа банковского терминала — нужен для возврата на карту. */
      rrn: string | null
    }) => Promise<{ refund: Refund; fiscal: FiscalRefundResult | null }>
  }
  /**
   * Операции с наличными внутри смены. Внесение при открытии и изъятие при закрытии
   * смены сюда не относятся — они остаются частью самой смены.
   */
  cashOps: {
    list: (shiftId?: number) => Promise<CashOp[]>
    add: (input: {
      kind: CashOpKind
      amount: number
      reason: string | null
      userId: number
      /**
       * Пробить фискальный чек внесения/изъятия. По умолчанию включено; main всё
       * равно перепроверяет, настроена ли касса, и рендереру на слово не верит.
       */
      fiscal: boolean
    }) => Promise<CashOp>
  }
  telegram: {
    test: () => Promise<{ ok: boolean; error: string | null }>
    getMe: () => Promise<{ ok: boolean; bot: TelegramBotInfo | null; error: string | null }>
  }
  journal: {
    list: (limit?: number) => Promise<JournalEntry[]>
    listCurrentShift: () => Promise<JournalEntry[]>
    add: (entry: Omit<JournalEntry, 'id' | 'ts'>) => Promise<JournalEntry>
    clear: () => Promise<void>
  }
  inventory: {
    listItems: (includeInactive?: boolean) => Promise<InventoryItem[]>
    upsertItem: (input: Omit<InventoryItem, 'id'> & { id?: number }) => Promise<InventoryItem>
    removeItem: (id: number) => Promise<void>
    /** Изменить только остаток позиции (clamp ≥0). Каталог приходит из бэкофиса pull'ом. */
    setStock: (id: number, stock: number) => Promise<InventoryItem>
    listForSession: (sessionId: number) => Promise<InventoryRented[]>
    issue: (sessionId: number, itemId: number, qty: number, userId: number) => Promise<InventoryTx>
    returnItem: (txId: number, userId: number) => Promise<void>
    returnAllForItem: (sessionId: number, itemId: number, userId: number) => Promise<number>
    history: (limit?: number) => Promise<InventoryTx[]>
  }
  customers: {
    list: (limit?: number) => Promise<Customer[]>
    search: (query: string) => Promise<Customer[]>
    byCode: (code: string) => Promise<Customer | null>
    byId: (id: number) => Promise<Customer | null>
    upsert: (
      input: Partial<
        Omit<
          Customer,
          'id' | 'createdAt' | 'levelName' | 'discountPercent' | 'bonusPercent' | 'totalSpent'
        >
      > & { id?: number; name: string }
    ) => Promise<Customer>
    remove: (id: number) => Promise<void>
    adjustBonus: (id: number, delta: number, note: string | null) => Promise<Customer>
    setPersonalDiscount: (
      id: number,
      percent: number,
      note: string | null,
      barDiscountEnabled?: boolean
    ) => Promise<Customer>
    history: (id: number, limit?: number) => Promise<LoyaltyTx[]>
    /** Брони клиента — вторая половина истории посещений рядом с сессиями. */
    bookings: (
      id: number,
      limit?: number
    ) => Promise<
      {
        id: number
        tableName: string
        startsAt: number
        endsAt: number
        status: string
        source: string | null
      }[]
    >
    sessions: (
      id: number,
      limit?: number
    ) => Promise<
      {
        id: number
        tableName: string
        tariffName: string
        startedAt: number
        endedAt: number | null
        amount: number
        discountAmount: number
        bonusUsed: number
        bonusEarned: number
      }[]
    >
    unlinkTelegram: (id: number) => Promise<Customer>
  }
  loyalty: {
    listLevels: () => Promise<LoyaltyLevel[]>
    upsertLevel: (input: Omit<LoyaltyLevel, 'id'> & { id?: number }) => Promise<LoyaltyLevel>
    removeLevel: (id: number) => Promise<void>
    listPromos: () => Promise<LoyaltyPromo[]>
    upsertPromo: (input: Omit<LoyaltyPromo, 'id'> & { id?: number }) => Promise<LoyaltyPromo>
    removePromo: (id: number) => Promise<void>
    /** Обнулить счётчик применений акции — «запустить акцию заново». */
    resetPromoUsage: (id: number, userId?: number | null) => Promise<LoyaltyPromo>
    quote: (customerId: number, amount: number) => Promise<LoyaltyQuote>
    /**
     * Скидки «из БО», доступные прямо сейчас БЕЗ клиента (активные акции с
     * discountPercent > 0). Для кнопки «Скидка %» в окне оплаты.
     */
    manualDiscountOptions: () => Promise<LoyaltyPromo[]>
  }
  coupons: {
    list: (includeRedeemed?: boolean) => Promise<Coupon[]>
    /** Выдать купон: генерирует уникальный код. */
    issue: (input: {
      kind: CouponKind
      discountPercent?: number
      freeMinutes?: number
      note?: string | null
      expiresAt?: number | null
      userId?: number | null
      barDiscountEnabled?: boolean
      /** Сколько раз купон можно применить (1…MAX_USAGE_LIMIT). По умолчанию 1. */
      usageLimit?: number
    }) => Promise<Coupon>
    /** Проверить код перед применением: бросает с текстом причины, если купон недействителен. */
    preview: (code: string) => Promise<Coupon>
    deactivate: (id: number, userId?: number | null) => Promise<Coupon>
    remove: (id: number) => Promise<void>
  }
  gameTypes: {
    /** Справочник типов игр клуба (синхронизируется из облака). */
    list: () => Promise<GameType[]>
  }
  bookings: {
    /** Предстоящие брони (status pending/confirmed/active, ends_at ≥ сейчас). */
    upcoming: (limit?: number) => Promise<TerminalBooking[]>
    /** Создать бронь на терминале (push в облако). Бросает при пересечении по времени. */
    create: (input: BookingCreateInput) => Promise<TerminalBooking>
    /** Перенести/изменить бронь. Бросает при пересечении по времени. */
    update: (id: number, patch: BookingUpdateInput) => Promise<TerminalBooking>
    /** Отменить бронь (status='cancelled'). */
    cancel: (id: number) => Promise<void>
    /** Сменить статус брони (active/no_show/completed/confirmed). */
    setStatus: (id: number, status: string) => Promise<void>
    /** Подписка на новые брони из облака (тост + звук). Возвращает отписку. */
    onNew: (cb: (b: NewBookingNotice) => void) => () => void
  }
  /**
   * Раздел «Поддержка»: обращение уходит в облако 8Ball, оттуда — в Help.
   * Без связи обращение кладётся в локальную очередь и уезжает само.
   */
  support: {
    list: (limit?: number) => Promise<SupportRequest[]>
    create: (input: {
      subject: string
      message: string
      category: SupportCategory
      contact?: string | null
      userId?: number | null
    }) => Promise<SupportRequest>
    /** Досыл очереди вручную (кнопка «Отправить снова»). Возвращает число отправленных. */
    flush: () => Promise<number>
    /** Обращения из облака с ответами поддержки. null — связи нет. */
    cloudTickets: () => Promise<SupportCloudTicket[] | null>
    /** Задачи, поставленные клубу поддержкой. Без связи — пусто. */
    tasks: () => Promise<SupportTask[]>
    /** Отметить задачу выполненной (уедет в Help). */
    completeTask: (taskId: number, userId?: number | null) => Promise<boolean>
    /**
     * Ответить в обращении. Ответ сначала ложится в локальную очередь (как и
     * обращение), автор — учётка, вошедшая в кассу.
     */
    reply: (input: {
      cloudTicketId: string
      message: string
      userId?: number | null
    }) => Promise<SupportReply>
    /** Локальные ответы (в том числе ещё не отправленные) — показать в переписке. */
    replies: () => Promise<SupportReply[]>
    /**
     * Сколько обращений с ответом поддержки, который на кассе ещё не открывали.
     * null — облако не ответило (нет связи): бейдж и звонок остаются как были.
     */
    unread: () => Promise<number | null>
    /** id обращения → время последнего ответа поддержки, который уже открывали. */
    seen: () => Promise<Record<string, number>>
    /** Обращение открыли — его ответы до lastReplyAt прочитаны. */
    markSeen: (cloudTicketId: string, lastReplyAt: number) => Promise<void>
  }
  kiosk: { unlock: (pin: string) => Promise<boolean> }
  /** Устройства зала (экраны кухни/бара), раздел «Устройства». */
  devices: {
    list: () => Promise<Device[]>
    /** Завести экран: PIN генерируется на стороне main. */
    add: (input: {
      name: string
      kind: DeviceKind
      deliveryEnabled?: boolean
      userId?: number | null
    }) => Promise<Device>
    update: (
      id: number,
      patch: { name?: string; kind?: DeviceKind; isActive?: boolean; deliveryEnabled?: boolean }
    ) => Promise<Device>
    /** Перевыпустить PIN (старый перестаёт работать сразу). */
    regeneratePin: (id: number) => Promise<Device>
    remove: (id: number) => Promise<void>
  }
  settings: {
    get: () => Promise<AppSettings>
    update: (patch: Partial<AppSettings>) => Promise<AppSettings>
  }
  printer: {
    listSystem: () => Promise<{ name: string; displayName?: string }[]>
    listPrinters: () => Promise<PrinterConfig[]>
    savePrinter: (cfg: PrinterConfig) => Promise<PrinterConfig[]>
    removePrinter: (id: string) => Promise<PrinterConfig[]>
    test: (printerId: string) => Promise<{ ok: boolean; error: string | null }>
    printReceipt: (payload: PrinterReceiptPayload) => Promise<{ ok: boolean; error: string | null }>
    printReport: (payload: PrinterReportPayload) => Promise<{ ok: boolean; error: string | null }>
    /**
     * Печать X/Z-отчёта по смене. Payload собирает main, поэтому X, ручной Z и
     * автопечать при закрытии смены гарантированно совпадают по содержимому.
     */
    /**
     * Печать нефискального чека по сессии; payload собирает main. Используется
     * после уведомления об успешной оплате картой и для перепечатки из «Чеков».
     */
    printSessionReceipt: (
      sessionId: number,
      userId?: number | null
    ) => Promise<{ ok: boolean; error: string | null }>
    printShiftReport: (
      shiftId: number,
      kind: 'X' | 'Z'
    ) => Promise<{ ok: boolean; error: string | null }>
    printBar: (payload: PrinterBarPayload) => Promise<{ ok: boolean; error: string | null }>
    printKitchen: (payload: PrinterKitchenPayload) => Promise<{ ok: boolean; error: string | null }>
    printPreCheck: (
      payload: PrinterPreCheckPayload
    ) => Promise<{ ok: boolean; error: string | null }>
  }
  fiscal: {
    test: () => Promise<{ ok: boolean; error: string | null }>
    status: () => Promise<FiscalStatus>
    discover: () => Promise<{ host: string; port: number }[]>
    /**
     * Повторить фискальный чек возврата. Сначала спрашивает кассу, не выполнена ли
     * операция уже (печать могла пройти, а ответ потеряться) — второй раз не пробивает.
     */
    retryRefund: (refundId: number) => Promise<FiscalRefundResult>
    /**
     * Пробить чек безнала ДО закрытия стола. При отказе кассы стол остаётся
     * открытым, и кассир может поменять раскладку списания и принять заново.
     * Состав чека собирается тем же кодом, что и при обычном закрытии.
     */
    printSaleForClose: (
      tableId: number,
      input: {
        amount: number
        barDiscountAmount: number
        cash: number
        cashless: number
        userId?: number | null
      }
    ) => Promise<FiscalSaleResult>
    printSale: (
      sessionId: number,
      amountOverride?: { cash: number; cashless: number }
    ) => Promise<FiscalSaleResult>
  }
  app: {
    info: () => Promise<AppInfo>
    openLogs: () => Promise<string>
    /**
     * Сообщить об ошибке интерфейса: пишется в локальный лог терминала и
     * уезжает в облако (серверный лог + Telegram-алерт). Вызывается из
     * глобальных обработчиков рендерера, не из бизнес-кода.
     */
    reportError: (input: {
      name: string
      message: string
      stack?: string
      context?: string
    }) => void
    /**
     * Окно свернули (или спрятали в Dock) при включённой блокировке по сворачиванию.
     * Решение «показать экран блокировки» принимает рендерер — main только
     * сообщает факт. Возвращает функцию отписки.
     */
    onMinimized: (cb: () => void) => () => void
  }
  updater: {
    /** Текущее состояние без обращения к сети. */
    status: () => Promise<UpdateStatus>
    /** Проверить наличие новой версии на фиде. */
    check: () => Promise<UpdateStatus>
    /** Скачать найденное обновление (прогресс приходит через onStatus). */
    download: () => Promise<UpdateStatus>
    /** Перезапустить приложение и установить скачанное обновление. */
    install: () => Promise<void>
    /** Подписка на изменения состояния (события и прогресс загрузки). */
    onStatus: (cb: (s: UpdateStatus) => void) => () => void
  }
  serial: {
    status: () => Promise<SerialStatus>
    listPorts: () => Promise<{ path: string; manufacturer?: string }[]>
    connect: (path: string, baud: number) => Promise<SerialStatus>
    disconnect: () => Promise<SerialStatus>
    send: (cmd: string) => Promise<string>
    /** Отправить команду без ожидания ответа (для переключения реле). */
    write: (cmd: string) => Promise<void>
    onLog: (
      cb: (entry: { dir: 's' | 'r' | 'e' | 'i'; text: string; ts: number }) => void
    ) => () => void
    onStatus: (cb: (s: SerialStatus) => void) => () => void
  }
  sync: {
    get: () => Promise<SyncConfigState>
    set: (input: SetSyncConfigInput) => Promise<SyncConfigState>
    /** Проверить связь с облаком (hello). input переопределяет сохранённые URL/токен. */
    test: (input?: SyncTestInput) => Promise<SyncTestResult>
    /** Немедленный блокирующий цикл push+pull (перед открытием смены). */
    now: () => Promise<SyncNowResult>
  }
  license: {
    /** Текущий статус лицензии (без сети, из sync_state). */
    status: () => Promise<LicenseStatus>
    /** Активировать терминал по лицензионному ключу (сетевой вызов). */
    activate: (baseUrl: string, key: string) => Promise<LicenseActivateResult>
    /** Зарегистрировать новый клуб самостоятельно (сетевой вызов, выдаёт пробную лицензию). */
    onboard: (baseUrl: string, input: OnboardClubInput) => Promise<OnboardResult>
    /** Отправить заявку на продление (сетевой вызов через device-токен). */
    requestRenewal: (note: string) => Promise<{ ok: boolean; error?: string }>
    /** Ссылка на оплату подписки картой — открывается в системном браузере. */
    payLink: () => Promise<LicensePayLinkResult>
    /** Подписка на изменения статуса (например, отзыв лицензии из облака). */
    onStatus: (cb: (s: LicenseStatus) => void) => () => void
  }
  bar: {
    /** Список активных категорий меню (синкнутых из облака). */
    listCategories: () => Promise<BarCategory[]>
    /** Список позиций меню, опционально фильтр по категории. */
    listItems: (categoryId?: string) => Promise<BarItem[]>
    /** Создать заказ бара для текущей сессии стола. */
    createOrder: (input: {
      tableId: number | null
      sessionId: number | null
      items: Array<{ barItemId: string; qty: number }>
    }) => Promise<BarOrder>
    /** Все заказы бара для сессии. */
    listOrdersForSession: (sessionId: number) => Promise<BarOrder[]>
    /** Заказы за период с фильтром по статусу исполнения — вкладка «Заказы». */
    listOrders: (input: {
      from: number
      to: number
      fulfillment?: BarOrderFulfillment | null
    }) => Promise<BarOrderRow[]>
    /**
     * Продажа бара/кухни БЕЗ игры (экран «Бар»). Оформляется чеком-сессией без
     * стола и тарифа, поэтому попадает в смену, Z-отчёт и облако как обычная
     * продажа. Требует открытой смены. Если в оплате есть безнал, возвращает
     * fiscalDue — рендерер добивает фискализацию с окном ожидания кассы.
     */
    sellWithoutGame: (input: {
      items: Array<{ barItemId: string; qty: number }>
      method: PaymentMethod
      cashAmount?: number
      userId: number | null
    }) => Promise<{
      sessionId: number
      amount: number
      fiscalDue: { cash: number; cashless: number } | null
      /** Печать чека отложена до уведомления об оплате картой (см. tables.stop). */
      receiptDeferred: boolean
    }>
    /** Складские позиции, спущенные из облака (ресторанный режим), read-only. */
    listStock: () => Promise<StockItem[]>
    /** Лукап складской позиции по штрихкоду (HID-сканер). null — не найдено. */
    stockByBarcode: (code: string) => Promise<StockItem | null>
    /**
     * Разбор скана: сначала ищем позицию меню (фасованный товар — сразу в заказ),
     * затем позицию склада (ингредиент — только показать остаток).
     */
    scanBarcode: (code: string) => Promise<BarcodeScanResult>
    /**
     * Ручной стоп/снятие со стопа позиции прямо с терминала (owner/admin, UI-гейт
     * в Bar.tsx). isStopped=false снимает и локальный ручной стоп, и локальный
     * isUnavailable — если стоп на самом деле держится ингредиентом техкарты в
     * облаке, следующий sync:pull вернёт позицию на стоп повторно.
     */
    setItemStop: (id: string, isStopped: boolean, reason: string | null) => Promise<BarItem | null>
  }
  /** Экран кухни (KDS): локальный сервер терминала + тикеты кухонных позиций. */
  kds: {
    /** Включён ли экран в настройках и слушает ли сервер (и на каком порту). */
    state: () => Promise<KdsState>
    /** Привести сервер в соответствие настройкам (после смены флага/порта). */
    sync: () => Promise<KdsState>
    listTickets: () => Promise<KitchenTicket[]>
    setStatus: (id: number, status: KitchenTicket['status']) => Promise<KitchenTicket | null>
  }
}
